"use client";

import { INPUT_MONO, TABLE, TD, TH } from "@/components/tool-kit/styles";

/*
 * A 2 × 2 table of count inputs — exposure by outcome, or test result by condition —
 * with the row and column totals beside it. Every word arrives through props; each
 * input carries a full spoken label ("Exposed, with the outcome (a)") because a grid of
 * bare boxes means nothing to a screen reader.
 */

export interface CountCell {
  id: string;
  /** The cell's full name, read by screen readers. */
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

export function TwoByTwoInput({
  caption,
  columnHeads,
  rowHeads,
  cells,
  totalLabel,
  totals,
}: {
  caption: string;
  columnHeads: readonly [string, string];
  rowHeads: readonly [string, string];
  /** Row by row: top-left, top-right, bottom-left, bottom-right. */
  cells: readonly [CountCell, CountCell, CountCell, CountCell];
  totalLabel: string;
  /** Row totals, column totals and the grand total, already formatted. */
  totals: { rows: [string, string]; columns: [string, string]; all: string };
}) {
  const rows = [
    [cells[0], cells[1]],
    [cells[2], cells[3]],
  ] as const;
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <caption className="px-3 pt-3 text-left text-muted-foreground text-sm">
          {caption}
        </caption>
        <thead>
          <tr>
            <td className={TH} />
            <th className={TH} scope="col">
              {columnHeads[0]}
            </th>
            <th className={TH} scope="col">
              {columnHeads[1]}
            </th>
            <th className={TH} scope="col">
              {totalLabel}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, r) => (
            <tr key={rowHeads[r]}>
              <th className={`${TD} font-medium`} scope="row">
                {rowHeads[r]}
              </th>
              {row.map((cell) => (
                <td className={TD} key={cell.id}>
                  <label className="sr-only" htmlFor={cell.id}>
                    {cell.label}
                  </label>
                  <input
                    className={`${INPUT_MONO} min-w-[5rem]`}
                    id={cell.id}
                    inputMode="numeric"
                    onChange={(event) => cell.onChange(event.target.value)}
                    placeholder={cell.placeholder}
                    type="text"
                    value={cell.value}
                  />
                </td>
              ))}
              <td className={`${TD} font-mono text-muted-foreground`}>
                {totals.rows[r]}
              </td>
            </tr>
          ))}
          <tr>
            <th className={`${TD} font-medium`} scope="row">
              {totalLabel}
            </th>
            <td className={`${TD} font-mono text-muted-foreground`}>
              {totals.columns[0]}
            </td>
            <td className={`${TD} font-mono text-muted-foreground`}>
              {totals.columns[1]}
            </td>
            <td className={`${TD} font-mono text-muted-foreground`}>
              {totals.all}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
